import React, { useEffect, useState } from "react";
import {
  Card,
  Grid,
  Icon,
  Tooltip,
  TextField,
  MenuItem,
  FormControl,
  Select,
  RadioGroup,
  Radio,
  FormControlLabel,
  Autocomplete,
  Chip,
} from "@mui/material";
import { ArrowDropDown } from "@mui/icons-material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import MDBadge from "components/MDBadge";
import CenterPopup from "components/CommonComponent/RightPanelDrawer";
import { useNotify } from "components/CommonComponent/NotificationProvider";
import { STATUS, hasAccess } from "components/common/constant";
import RequiredLabel from "components/CommonComponent/RequiredLabel";
import { showAlert } from "components/CommonComponent/ShowAlert";
import {
  selectSx,
  radioLabelSx,
  autoCompleteProps,
  wrapCell,
} from "components/CommonComponent/CommonFunction";
import {
  deleteProjectById,
  getProjectList,
  updateProjectById,
  addProject,
  getProjectById,
  importProject,
  exportProjectData,
} from "actions/project";
import { useDispatch, useSelector } from "react-redux";
import { getUnitListing } from "actions/unit";
import { unitList } from "constants/unit";
import { getProductList } from "actions/product";
import { productList } from "constants/product";
import { customerList } from "constants/customer";
import { getCustomerList } from "actions/customer";
import { projectList } from "constants/project";
import { DEFAULT_RECORDS_PER_PAGE } from "components/common/constant";

const initialFields = {
  projectCode: {
    value: "",
    error: "",
    isChange: false,
  },
  typeOfProduct: {
    value: "",
    error: "",
    isChange: false,
  },
  customerName: {
    value: "",
    error: "",
    isChange: false,
  },
  unitNumber: {
    value: [],
    error: "",
    isChange: false,
  },
  rating: {
    value: "",
    error: "",
    isChange: false,
  },
  status: {
    value: "ACTIVE",
    error: "",
    isChange: false,
  },
  subUnit: {
    value: [],
    error: "",
    isChange: false,
  },
};

function ProjectListing() {
  const dispatch = useDispatch();
  //project data
  const projectListData = useSelector((state) => state.project.projectList);
  //customer data
  const customerListData = useSelector((state) => state.customer.customerList);
  //product data
  const productListData = useSelector((state) => state.product.productList);
  //unit data
  const unitListingData = useSelector((state) => state.unit.unitList);

  const authData = JSON.parse(localStorage.getItem("p")) || {};
  const currentRole = authData?.role;

  const canCreate = hasAccess("PROJECT_MASTER", "CREATE")?.includes(
    currentRole,
  );
  const canUpdate = hasAccess("PROJECT_MASTER", "UPDATE")?.includes(
    currentRole,
  );
  const canDelete = hasAccess("PROJECT_MASTER", "DELETE")?.includes(
    currentRole,
  );

  const canShowActions = canUpdate || canDelete;

  const [panelMode, setPanelMode] = useState(null); // add | edit
  const [formData, setFormData] = useState(initialFields);
  const [isUsed, setIsUsed] = useState(false);
  const [pageNum, setPageNum] = useState(1);
  const { notify } = useNotify();
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });

  const [filters, setFilters] = useState({
    projectCode: "",
    customer: null,
    unit: null,
    typeOfProduct: null,
    subUnits: "",
    rating: "",
    status: null,
    filterParams: [],
  });

  const [exportLoading, setExportLoading] = useState(false);

  const customers = customerListData?.data?.Customer || [];
  const products = productListData?.data?.Product || [];
  const units = unitListingData?.data?.Unit || [];

  useEffect(() => {
    getCustomerList(dispatch);
    getProductList(dispatch);
    getUnitListing(dispatch);
    return () => {
      dispatch({ type: customerList, payload: {} });
      dispatch({ type: productList, payload: {} });
      dispatch({ type: unitList, payload: {} });
    };
  }, [dispatch]);

  //api to fetch project list
  useEffect(() => {
    getProjectList(dispatch, pageNum, filters.filterParams);
    return () => {
      dispatch({ type: projectList, payload: {} });
    };
  }, [dispatch, pageNum, filters.filterParams]);

  // check the listing data state using code and message
  useEffect(() => {
    if (projectListData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: projectListData?.data?.project?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (projectListData?.code || projectListData?.message) {
      setListState({
        isLoading: false,
        error:
          projectListData?.message ||
          "Failed to load project list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [projectListData]);

  const formattedListData = projectListData?.data?.project?.map(
    (item, index) => ({
      id: item.ID,
      code: item.projectNo || "-",
      typeOfProduct: item.product?.type || "-",
      typeOfProductId: item.product?.ID,
      customerName: item.customer?.name || "-",
      customerId: item.customer?.ID,
      unitIds: item.units?.map((u) => u.ID) || [],
      unitNames:
        item?.units?.length > 0
          ? item.units.map((u) => u.unitNo).join(", ")
          : "-",

      subUnits: item?.subUnits?.length > 0 ? item.subUnits.join(", ") : "-",
      rating: item?.rating ?? "-",
      status: item.status || "-",
      srNo:
        (pageNum - 1) * (projectListData?.data?.noOfRecordsPerPage || 10) +
        index +
        1,
    }),
  );

  const getValidationError = (name, value) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    switch (name) {
      case "projectCode":
        return !trimmed ? "Please enter project number" : "";
      case "typeOfProduct":
        return !trimmed ? "Please select type of product" : "";
      case "customerName":
        return !trimmed ? "Please enter customer name" : "";
      case "unitNumber":
        return !value || value.length === 0 ? "Please select unit number" : "";

      case "rating":
        return "";

      case "subUnit":
        return !value || value.length === 0
          ? "Please enter at least one sub unit"
          : "";

      default:
        return "";
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        value,
        error: "",
        isChange: true,
      },
    }));
  };

  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const validateForm = () => {
    let newData = { ...formData };
    Object.keys(newData).forEach((key) => {
      newData[key] = {
        ...newData[key],
        error: getValidationError(key, newData[key].value),
      };
    });
    setFormData(newData);
    return Object.values(newData).every((item) => item.error === "");
  };

  const openAddModal = () => {
    setPanelMode("add");
    setFormData(initialFields);
    setIsUsed(false);
  };

  const openEditModal = async (row) => {
    setPanelMode("edit");
    setListState((prev) => ({
      ...prev,
      isLoading: true,
    }));

    try {
      const res = await getProjectById(row.id);
      if (res?.code === 200) {
        const project = res?.data;

        setIsUsed(project?.isUsed ?? false);

        setFormData({
          id: { value: project?.ID },
          projectCode: {
            value: project?.projectNo || "",
            error: "",
            isChange: false,
          },
          typeOfProduct: {
            value: products.find((p) => p.ID === project?.product?.ID) || null,
            error: "",
            isChange: false,
          },
          customerName: {
            value:
              customers.find((c) => c.ID === project?.customer?.ID) || null,
            error: "",
            isChange: false,
          },
          unitNumber: {
            value: units.filter((u) =>
              project?.units?.some((pu) => pu.ID === u.ID),
            ),
            error: "",
            isChange: false,
          },
          rating: {
            value: project?.rating || "",
            error: "",
            isChange: false,
          },
          subUnit: {
            value: project?.subUnits || [],
            error: "",
            isChange: false,
          },
          status: { value: row.status, error: "", isChange: false },
        });
      } else {
        notify(res?.message || "Failed to fetch project details", "error");
        closeModal();
      }
    } catch (error) {
      notify("Something went wrong while fetching project details", "error");
      closeModal();
    } finally {
      setListState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const closeModal = () => {
    setPanelMode(null);
    setFormData({ ...initialFields });
    setIsUsed(false);
  };

  const handleDelete = (row) => {
    showAlert({
      title: `Are you sure you want to delete this project?`,
      message: `Project "${row.name}" will be deleted permanently.`,
      type: "warning",
      showCancel: true,
      confirmText: "Yes, delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          setListState((prev) => ({
            ...prev,
            isLoading: true,
          }));

          const res = await deleteProjectById(row.id);

          if (res?.code === 200) {
            notify(res?.message || "Project deleted successfully", "success");
            pageNum === 1
              ? getProjectList(dispatch, pageNum, filters.filterParams)
              : setPageNum(1);
          } else {
            notify(res?.message || "Failed to delete project", "error");
          }
        } catch (error) {
          notify("Something went wrong while deleting project", "error");
        } finally {
          setListState((prev) => ({
            ...prev,
            isLoading: false,
          }));
        }
      },
      onCancel: () => {},
    });
  };

  const getEditedBodyData = (formData) =>
    Object.fromEntries(
      Object.entries(formData)
        .filter(([, field]) => field?.isChange)
        .map(([key, field]) => {
          switch (key) {
            case "projectCode":
              return ["projectNo", field.value];

            case "typeOfProduct":
              return ["productID", field.value?.ID];

            case "customerName":
              return ["customerID", field.value?.ID];

            case "unitNumber":
              return ["unitIDs", field.value?.map((u) => u.ID)];

            case "status":
              return ["status", field.value];

            case "rating":
              return [
                "rating",
                field.value !== "" && field.value != null
                  ? parseFloat(field.value)
                  : null,
              ];

            case "subUnit":
              return ["subUnits", field.value];

            default:
              return null;
          }
        })
        .filter(Boolean),
    );

  const handleSubmit = async () => {
    if (!validateForm()) return;
    const isEdit = panelMode === "edit";

    let bodyData = {
      projectNo: formData.projectCode.value,
      productID: formData.typeOfProduct.value?.ID,
      customerID: formData.customerName.value?.ID,
      unitIDs: formData.unitNumber.value?.map((u) => u.ID),
      status: formData.status.value,
      rating:
        formData.rating.value !== "" && formData.rating.value != null
          ? parseFloat(formData.rating.value)
          : null,
      subUnits: formData.subUnit.value,
    };

    if (isEdit) {
      const editedData = getEditedBodyData(formData);
      if (Object.keys(editedData).length === 0) {
        notify("No changes detected", "info");
        return;
      }
      bodyData = editedData;
    }

    try {
      setListState((prev) => ({
        ...prev,
        showLoaderOnClick: true,
      }));

      const res = isEdit
        ? await updateProjectById(formData.id.value, bodyData)
        : await addProject(bodyData);

      if (res?.code === 200) {
        notify(
          res?.message ||
            `Project ${isEdit ? "updated" : "added"} successfully`,
          "success",
        );
        closeModal();
        // pageNum === 1
           getProjectList(dispatch, pageNum, filters.filterParams)
          // : setPageNum(1);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Failed to save project";

        notify(message, "error");
      }
    } catch (error) {
      notify(`Failed to ${isEdit ? "update" : "add"} project`, "error");
    } finally {
      setListState((prev) => ({
        ...prev,
        showLoaderOnClick: false,
      }));
    }
  };

  const renderStatusBadge = (status) => {
    if (!status) return null;
    const s = typeof status === "string" ? status.toUpperCase() : status;
    return (
      <MDBadge
        badgeContent={s}
        color={s === "ACTIVE" ? "success" : "secondary"}
        variant="gradient"
        size="sm"
        circular
      />
    );
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    // only csv validation
    if (!file.name.toLowerCase().endsWith(".csv")) {
      notify("Please upload a valid CSV file", "error");
      e.target.value = null;
      return;
    }

    try {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
      }));

      const formData = new FormData();
      formData.append("file", file);

      const res = await importProject(formData);

      if (res?.code === 200) {
        notify(res?.message || "Project CSV imported successfully", "success");

        // refresh listing
        getProjectList(dispatch, pageNum, filters.filterParams);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message.map((err) => err.Msg || JSON.stringify(err)).join(", ")
          : res?.message || "Failed to import project CSV";

        notify(message, "error");
      }
    } catch (error) {
      notify(
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong while importing CSV",
        "error",
      );
    } finally {
      setListState((prev) => ({
        ...prev,
        isLoading: false,
      }));

      // reset input so same file can be selected again
      e.target.value = null;
    }
  };

  const downloadProjectCSV = async () => {
    try {
      setExportLoading(true);

      const res = await exportProjectData(filters.filterParams);
      const url = window.URL.createObjectURL(res.data);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Projects.csv");

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      notify(res?.message || "Project file downloaded successfully","success");
    } catch (error) {
      notify("Failed to download project file","error");
    } finally {
      setExportLoading(false);
    }
  };

  const downloadSampleCsv = () => {
    const csvContent = `ID,Product Type,Project No.,Customer Name,Units,Sub-Units,Rating
1,OFT,ADA1728103,JSW Steel,9,"U, V, W",31.5
2,OFT,ADA1728105,JSW Steel,3,"U, V, W",31.5
3,OFT,ADA1823102,Truvolt,11,"U, V, W",10
4,OFT,ADA1725106,Onword Solar Power,4,"U, V, W",13.2`;

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", "sample_project.csv");
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const columns = [
    { Header: "Sr. No.", accessor: "srNo", width: "100px" },
    {
      Header: "Project No",
      accessor: "code",
      width: "130px",
      Cell: (cell) => wrapCell(cell.value, "130px"),
    },
    {
      Header: "Product Type",
      accessor: "typeOfProduct",
      width: "140px",
      Cell: (cell) => wrapCell(cell.value, "140px"),
    },
    {
      Header: "Customer Name",
      accessor: "customerName",
      width: "180px",
      Cell: (cell) => wrapCell(cell.value, "180px"),
    },
    {
      Header: "Unit Number",
      accessor: "unitNames",
      width: "135px",
      Cell: (cell) => wrapCell(cell.value, "130px"),
    },
    {
      Header: "Sub Units",
      accessor: "subUnits",
      width: "135px",
      Cell: (cell) => wrapCell(cell.value, "130px"),
    },
    {
      Header: "Rating",
      accessor: "rating",
      width: "100px",
    },
    {
      Header: "Status",
      accessor: "status",
      align: "center",
      width: "120px",
      Cell: (cell) => renderStatusBadge(cell.row.original.status),
    },

    ...(canShowActions
      ? [
          {
            Header: "Action",
            accessor: "actions",
            align: "center",
            disableSortBy: true,
            Cell: (cell) => {
              const rowData = cell.row.original;

              return (
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={1}
                >
                  {canUpdate && (
                    <Tooltip title="Edit" arrow>
                      <Icon
                        sx={({ palette }) => ({
                          cursor: "pointer",
                          color: palette.secondary.main,
                        })}
                        onClick={() => openEditModal(rowData)}
                      >
                        <i className="fas fa-edit fa-sm" />
                      </Icon>
                    </Tooltip>
                  )}

                  {canUpdate && canDelete && (
                    <MDTypography variant="caption" mx={0.5}>
                      |
                    </MDTypography>
                  )}

                  {canDelete && (
                    <Tooltip title="Delete" arrow>
                      <Icon
                        color="error"
                        sx={{
                          cursor: "pointer",
                          opacity: 0.5,
                          "&:hover": { opacity: 1 },
                        }}
                        onClick={() => handleDelete(rowData)}
                      >
                        <i className="fas fa-trash" />
                      </Icon>
                    </Tooltip>
                  )}
                </MDBox>
              );
            },
          },
        ]
      : []),
  ];

  // filters for search
  const buildFilterParams = () => {
    const filterParams = [];

    if (filters.projectCode) {
      filterParams.push({
        field: "ProjectNo",
        condition: "like",
        filterValues: [filters.projectCode],
      });
    }

    if (filters.customer) {
      filterParams.push({
        field: "CustomerID",
        condition: "eq",
        filterValues: [String(filters.customer)],
      });
    }

    if (filters.unit) {
      filterParams.push({
        field: "UnitID",
        condition: "eq",
        filterValues: [String(filters.unit)],
      });
    }

    if (filters.subUnits) {
      filterParams.push({
        field: "SubUnit",
        condition: "like",
        filterValues: [filters.subUnits],
      });
    }
    if (filters.rating) {
      filterParams.push({
        field: "Rating",
        condition: "eq",
        filterValues: [filters.rating],
      });
    }

    if (filters.typeOfProduct) {
      filterParams.push({
        field: "ProductID",
        condition: "eq",
        filterValues: [String(filters.typeOfProduct)],
      });
    }

    if (filters.status) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [filters.status],
      });
    }
    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const handleSearch = () => {
    const newfilterParams = buildFilterParams();

    if (
      JSON.stringify(filters.filterParams) !== JSON.stringify(newfilterParams)
    ) {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));
      setFilters((prev) => ({
        ...prev,
        filterParams: newfilterParams,
      }));

      if (pageNum !== 1) setPageNum(1);
    }
  };

  const handleClear = () => {
    setFilters({
      projectCode: "",
      customer: null,
      unit: null,
      typeOfProduct: null,
      status: null,
      rating: "",
      subUnits: "",
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox width="100%">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ width: "100%" }}>
              {/* Header */}
              <MDBox
                px={3}
                pt={3}
                pb={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h5" fontWeight="bold">
                  Projects
                </MDTypography>

                {canCreate && (
                  <MDBox display="flex" gap={2}>
                    <MDButton
                      variant="gradient"
                      color="light"
                      startIcon={<Icon>file_download</Icon>}
                      sx={{
                        py: 1.2,
                        px: 3,
                        fontWeight: "bold",
                        textTransform: "none",
                      }}
                      onClick={downloadSampleCsv}
                    >
                      Sample CSV
                    </MDButton>
                    <MDButton
                      variant="gradient"
                      color="light"
                      startIcon={<Icon>file_upload</Icon>}
                      sx={{
                        py: 1.2,
                        px: 3,
                        fontWeight: "bold",
                        textTransform: "none",
                      }}
                      onClick={downloadProjectCSV}
                    >
                      {exportLoading ? (
                        <>
                          <i className="fa fa-spinner fa-spin mr-2" />
                          Exporting...
                        </>
                      ) : (
                        "Export Project"
                      )}
                    </MDButton>

                    <MDButton
                      variant="gradient"
                      color="light"
                      component="label"
                      startIcon={<Icon>download</Icon>}
                      sx={{
                        py: 1.2,
                        px: 3,
                        fontWeight: "bold",
                        textTransform: "none",
                      }}
                    >
                      Import CSV
                      <input
                        type="file"
                        hidden
                        accept=".csv"
                        onChange={handleCsvUpload}
                      />
                    </MDButton>

                    <MDButton
                      type="button"
                      variant="contained"
                      color="deepBlue"
                      onClick={openAddModal}
                      sx={{
                        py: 1.2,
                        px: 3,
                        fontWeight: "bold",
                        fontSize: "0.9rem",
                        textTransform: "none",
                      }}
                    >
                      Add Project
                    </MDButton>
                  </MDBox>
                )}
              </MDBox>

              <MDBox px={3} pb={2} onKeyDown={handleKeyDown}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search by project number"
                      value={filters.projectCode}
                      onChange={(e) =>
                        setFilters({ ...filters, projectCode: e.target.value })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      size="small"
                      options={products}
                      getOptionLabel={(option) => option.type || ""}
                      value={
                        products.find((p) => p.ID === filters.typeOfProduct) ||
                        null
                      }
                      onChange={(e, newValue) =>
                        setFilters({
                          ...filters,
                          typeOfProduct: newValue?.ID || null,
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.ID === value?.ID
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select product type"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      size="small"
                      options={customers}
                      getOptionLabel={(option) => option.name || ""}
                      value={
                        customers.find((c) => c.ID === filters.customer) || null
                      }
                      onChange={(e, newValue) =>
                        setFilters({
                          ...filters,
                          customer: newValue?.ID || null,
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.ID === value?.ID
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select customer" />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      size="small"
                      options={units}
                      getOptionLabel={(option) => option.unitNo || ""}
                      value={units.find((u) => u.ID === filters.unit) || null}
                      onChange={(e, newValue) =>
                        setFilters({
                          ...filters,
                          unit: newValue?.ID || null,
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.ID === value?.ID
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select unit" />
                      )}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2} mt={0.2} alignItems="center">
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search by sub unit "
                      value={filters.subUnits}
                      onChange={(e) =>
                        setFilters({ ...filters, subUnits: e.target.value })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search by rating"
                      value={filters.rating}
                      onChange={(e) =>
                        setFilters({ ...filters, rating: e.target.value })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <FormControl fullWidth size="small" sx={selectSx("100%")}>
                      <Select
                        value={filters.status || ""}
                        displayEmpty
                        IconComponent={ArrowDropDown}
                        onChange={(e) =>
                          setFilters({ ...filters, status: e.target.value })
                        }
                        renderValue={(selected) =>
                          !selected ? (
                            <span style={{ color: "rgba(0, 0, 0, 0.38)" }}>
                              Select Status
                            </span>
                          ) : (
                            renderStatusBadge(selected)
                          )
                        }
                      >
                        {STATUS.map((s) => (
                          <MenuItem key={s.value} value={s.value}>
                            {renderStatusBadge(s.value)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                    <MDBox
                      display="flex"
                      gap={1}
                      flexWrap="nowrap"
                      width="100%"
                    >
                      <MDButton
                        variant="contained"
                        color="deepBlue"
                        sx={{
                          flex: 1,
                          minWidth: "90px",
                          maxWidth: "160px",
                          whiteSpace: "nowrap",
                        }}
                        onClick={handleSearch}
                        disabled={listState.showLoaderOnClick}
                      >
                        {listState.showLoaderOnClick ? (
                          <i className="fa fa-spinner fa-spin fa-lg text-white" />
                        ) : (
                          <>
                            <Icon sx={{ mr: 1 }}>search</Icon>
                            Search
                          </>
                        )}
                      </MDButton>

                      {filters.filterParams.length > 0 && (
                        <MDButton
                          variant="contained"
                          color="deepBlue"
                          sx={{
                            flex: 1,
                            minWidth: "90px",
                            maxWidth: "160px",
                            whiteSpace: "nowrap",
                          }}
                          onClick={handleClear}
                        >
                          <Icon sx={{ mr: 1 }}>close</Icon>
                          Clear
                        </MDButton>
                      )}
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>

              {/* Total Records */}
              <MDBox px={3} py={1}>
                <MDTypography variant="body1" color="deepBlue" mb={1}>
                  Total Records: {projectListData?.data?.totalRecords || 0}
                </MDTypography>
              </MDBox>

              {/* Table */}
              <MDBox sx={{ width: "100%", overflowX: "auto" }}>
                {listState.isLoading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </MDBox>
                ) : listState.error ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography color="error" variant="body2">
                      {listState.error}
                    </MDTypography>
                  </MDBox>
                ) : listState.noRecords ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="20vh"
                  >
                    <MDTypography color="text" variant="body2">
                      No projects found.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    columns={columns}
                    data={formattedListData || []}
                    isSorted
                    pageNum={pageNum}
                    setPageNum={setPageNum}
                    totalPages={Math.ceil(
                      (projectListData?.data?.totalRecords || 0) /
                        (projectListData?.data?.noOfRecordsPerPage ||
                          DEFAULT_RECORDS_PER_PAGE),
                    )}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        <CenterPopup
          isOpen={panelMode !== null}
          onClose={closeModal}
          width={650}
        >
          <MDBox sx={{ p: 2 }}>
            <MDTypography variant="h6" mb={0}>
              {panelMode === "edit" ? "Edit Project" : "Add Project"}
            </MDTypography>

            {panelMode === "edit" && isUsed && (
              <Grid item xs={12} sx={{ mb: 1 }}>
                <MDTypography
                  variant="caption"
                  sx={{
                    color: "#fc6e0f",
                  }}
                >
                  <b>Note:</b> This project is used in timesheets, so Product
                  Type, Project Number, Customer Name, Unit Number, and Sub Unit
                  cannot be edited.
                </MDTypography>
              </Grid>
            )}

            {listState.isLoading ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="150px"
              >
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </MDBox>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <RequiredLabel required>Product Type</RequiredLabel>

                  <Autocomplete
                    {...autoCompleteProps}
                    size="small"
                    disabled={panelMode === "edit" && isUsed}
                    options={products}
                    getOptionLabel={(option) => option.type || ""}
                    value={formData.typeOfProduct.value || null}
                    onChange={(e, newValue) =>
                      handleChange("typeOfProduct", newValue)
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?.ID === value?.ID
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select type of product"
                        error={!!formData.typeOfProduct.error}
                        helperText={formData.typeOfProduct.error}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <RequiredLabel required>Project Number</RequiredLabel>
                  <MDInput
                    variant="outlined"
                    type="text"
                    name="projectCode"
                    placeholder="Enter project number"
                    value={formData.projectCode.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    error={!!formData.projectCode.error}
                    fullWidth
                    disabled={panelMode === "edit" && isUsed}
                    inputProps={{ maxLength: 50 }}
                    InputProps={{
                      sx: { borderRadius: 2 },
                    }}
                  />
                  {formData.projectCode.error && (
                    <MDTypography variant="caption" color="error">
                      {formData.projectCode.error}
                    </MDTypography>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <RequiredLabel required>Customer Name</RequiredLabel>
                  <Autocomplete
                    {...autoCompleteProps}
                    size="small"
                    disabled={panelMode === "edit" && isUsed}
                    options={customers}
                    getOptionLabel={(option) => option.name || ""}
                    value={formData.customerName.value || null}
                    onChange={(e, newValue) =>
                      handleChange("customerName", newValue)
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?.ID === value?.ID
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select customer"
                        error={!!formData.customerName.error}
                      />
                    )}
                  />
                  {formData.customerName.error && (
                    <MDTypography variant="caption" color="error">
                      {formData.customerName.error}
                    </MDTypography>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <RequiredLabel required>Unit Number</RequiredLabel>
                  <Autocomplete
                    {...autoCompleteProps}
                    multiple
                    size="small"
                    options={units}
                    getOptionLabel={(option) => option.unitNo || ""}
                    value={formData.unitNumber.value || []}
                    onChange={(e, newValue) =>
                      handleChange("unitNumber", newValue)
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?.ID === value?.ID
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select unit no"
                        error={!!formData.unitNumber.error}
                        helperText={formData.unitNumber.error}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <RequiredLabel required>Sub Unit</RequiredLabel>

                  <Autocomplete
                    multiple
                    freeSolo
                    size="small"
                    options={[]} // no predefined options
                    value={formData.subUnit?.value || []}
                    onChange={(event, newValue) => {
                      handleChange("subUnit", newValue);
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={index}
                          label={option}
                          size="small"
                          sx={{
                            backgroundColor: "#e0e0e0",
                            borderRadius: "6px",
                            m: "2px",
                          }}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Type sub unit and press Enter"
                        error={!!formData.subUnit.error}
                        helperText={formData.subUnit.error}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <RequiredLabel>Rating</RequiredLabel>
                  <MDInput
                    variant="outlined"
                    type="text"
                    name="rating"
                    placeholder="Enter rating"
                    value={formData.rating.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    error={!!formData.rating.error}
                    fullWidth
                    inputProps={{ maxLength: 100 }}
                    InputProps={{
                      sx: { borderRadius: 2 },
                    }}
                  />
                  {formData.rating.error && (
                    <MDTypography variant="caption" color="error">
                      {formData.rating.error}
                    </MDTypography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <RequiredLabel>Status</RequiredLabel>
                  <RadioGroup
                    row
                    name="status"
                    value={formData.status.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                  >
                    <FormControlLabel
                      value="ACTIVE"
                      control={<Radio size="small" />}
                      label="Active"
                      sx={radioLabelSx}
                    />
                    <FormControlLabel
                      value="INACTIVE"
                      control={<Radio size="small" />}
                      label="Inactive"
                      sx={radioLabelSx}
                    />
                  </RadioGroup>
                </Grid>

                {/* Save / Cancel */}
                <Grid item xs={12}>
                  <MDBox display="flex" justifyContent="flex-end">
                    <MDButton
                      variant="gradient"
                      color="light"
                      onClick={closeModal}
                      sx={{ mr: 2 }}
                    >
                      Cancel
                    </MDButton>

                    <MDButton
                      type="submit"
                      variant="contained"
                      color="deepBlue"
                      onClick={handleSubmit}
                      disabled={listState.showLoaderOnClick}
                    >
                      {listState.showLoaderOnClick ? (
                        <>
                          <i className="fa fa-spinner fa-spin fa-lg text-white" />
                        </>
                      ) : (
                        "Save"
                      )}
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            )}
          </MDBox>
        </CenterPopup>
      </MDBox>
    </DashboardLayout>
  );
}

export default ProjectListing;
